import { Camera, Mesh, Scene, Vector3 } from "three";
import { useFrame } from "@react-three/fiber";
import { useCallback, useRef } from "react";
import { MAP_MODE } from "../../DevConstants";
import { MeshFeatureContextHook } from "../MeshFeatureContext";
import { PhysicalContexts } from "./CollisionTypes";
import { rayIntersects } from "./CollisionUtils";

const OmitFromCameraPhysicalContexts = ["Wall", "ComplexWall", "Player"];

const CAMERA_OFFSET = {
  x: 0,
  y: 1,
  z: 4,
};

export const positionWithinThreshold = (
  pos1: Vector3,
  pos2: Vector3,
  threshold: number
) => {
  return (
    Math.abs(pos1.x - pos2.x) < threshold &&
    Math.abs(pos1.y - pos2.y) < threshold &&
    Math.abs(pos1.z - pos2.z) < threshold
  );
};

export const useCameraFollow: MeshFeatureContextHook = (mesh: Mesh) => {
  const firstRunRef = useRef(true);
  const getMeshGlobalPos = useCallback(
    () => (mesh ? mesh.localToWorld(new Vector3()) : new Vector3()),
    [mesh]
  );
  const getNewCamPos = useCallback(
    () =>
      getMeshGlobalPos().add(
        new Vector3(CAMERA_OFFSET.x, CAMERA_OFFSET.y, CAMERA_OFFSET.z)
      ),
    [getMeshGlobalPos]
  );
  const getTargetPoint = useCallback(
    (scene: Scene, camera: Camera) => {
      const newCamPos = getNewCamPos();
      const intersects = rayIntersects(
        newCamPos,
        camera.position.clone().sub(newCamPos).normalize(),
        newCamPos.distanceTo(camera.position),
        scene,
        PhysicalContexts,
        OmitFromCameraPhysicalContexts
      );

      return intersects ? camera.position : newCamPos;
    },
    [getNewCamPos]
  );

  useFrame(({ scene, camera }) => {
    if (!MAP_MODE && mesh && camera) {
      const lookPos = getMeshGlobalPos();
      const targetPoint = getTargetPoint(scene, camera);

      if (firstRunRef.current) {
        firstRunRef.current = false;

        camera.position.copy(targetPoint);
      }

      camera.position.lerp(targetPoint, 0.1);

      lookPos.x = camera.position.x;

      camera.lookAt(lookPos);
    }
  });
};
